<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'Add Category'">
      <template #left>
        <font-awesome-icon
          @click="
            store.addSubcategoryToEditCategory
              ? router.push({ name: 'EditCategory' })
              : router.push({ name: 'Categories' })
          "
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
        />
      </template>
    </MainHeader>

    <div class="view ac-view">
      <div v-if="!store.addSubcategoryToEditCategory" class="et-tabs">
        <button
          class="et-tab"
          :class="{ active: !subcategorySliderSelected }"
          @click="setMode(false)"
        >
          Add Category
        </button>
        <button
          class="et-tab"
          :class="{ active: subcategorySliderSelected }"
          @click="setMode(true)"
        >
          Add Subcategory
        </button>
      </div>

      <div class="ac-header">
        <h1 class="ac-title">
          {{ subcategorySliderSelected ? 'New Subcategory' : 'New Category' }}
        </h1>
        <p class="ac-sub">
          {{
            subcategorySliderSelected
              ? 'Add a subcategory to help organise your transactions further.'
              : 'Create a category to start organising your transactions.'
          }}
        </p>
      </div>

      <form @submit.prevent="createCategory">
        <fieldset>
          <!-- Details section -->
          <div class="ud-section">
            <span class="section-label">Details</span>

            <div class="ud-field" v-if="subcategorySliderSelected">
              <label class="ud-label" for="parentcategory">
                Parent Category <span class="ud-asterisk">*</span>
              </label>
              <select
                v-model="selectedParentCategory"
                class="ud-input ud-select form-select"
                id="parentcategory"
                required
              >
                <option value="" disabled selected>Select a category</option>
                <option
                  v-for="parentCategory in store.categories"
                  :key="parentCategory"
                  :value="parentCategory"
                  :selected="parentCategory"
                >
                  {{ parentCategory.name }}
                </option>
              </select>
            </div>

            <div class="ud-field mt">
              <label class="ud-label" for="categoryname">
                Name <span class="ud-asterisk">*</span>
              </label>
              <input
                id="categoryname"
                type="text"
                class="ud-input"
                v-model="categoryName"
                placeholder="e.g. Food & Dining"
                required
              />
            </div>

            <div class="ud-field mt">
              <label class="ud-label" for="defaultbudget">
                Default Budget <span class="ud-asterisk">*</span>
              </label>
              <input
                id="defaultbudget"
                type="number"
                class="ud-input"
                v-model="defaultBudget"
                step="0.01"
                min="0"
                placeholder="0.00"
                required
              />
              <span class="ud-hint">
                The amount you typically expect to
                {{ incomeCategory ? 'earn' : 'spend' }} each month. Auto-fills
                in new monthly budgets.
              </span>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Appearance</span>

            <div class="ud-field">
              <label class="ud-label">Category Icon</label>
              <IconSearch
                button-text="Choose Icon"
                styleClass="form-dropdown"
                @optionSelected="updateSelectedIcon($event)"
              />
            </div>

            <div class="ud-field mt">
              <label class="ud-label" for="categorycolor">Category Color</label>
              <ColorSelection
                id="categorycolor"
                button-text="Choose Color"
                styleClass="form-dropdown"
                @optionSelected="updateSelectedIconColor($event)"
              />
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Auto-categorization rules</span>
            <div class="expandable-section">
              <div
                class="expandable-header"
                @click="showAutoCategorization = !showAutoCategorization"
              >
                <span class="ud-label">Apply Rules</span>
                <font-awesome-icon
                  :icon="['fas', 'caret-down']"
                  class="expand-caret"
                  :class="{ expanded: showAutoCategorization }"
                />
              </div>
              <transition name="sub-slide">
                <div v-if="showAutoCategorization" class="expandable-body">
                  <button
                    aria-label="Add Rule"
                    role="button"
                    class="outline-button w-100 mb-1"
                    type="button"
                    @click="createNewRule"
                    data-pt-name="AutoCategorizationRuleCreated"
                  >
                    + Add Rule
                  </button>
                  <div v-for="(rule, index) in ruleItems" :key="rule">
                    <Rule
                      :currentRule="ruleItems[index].rule"
                      @addRule="createNewRule"
                      @updateRule="(rule) => updateRulesList(index, rule)"
                      @deleteRule="(rule) => deleteFromRulesList(index, rule)"
                    />
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <div class="ud-section" v-if="!subcategorySliderSelected">
            <span class="section-label">Settings</span>
            <div class="toggle-row">
              <span class="ud-label">Mark as Income</span>
              <Toggle id="isIncome" @toggleSelected="toggleIncome($event)" />
            </div>
          </div>

          <button
            aria-label="Create Category"
            role="button"
            class="ud-submit"
            type="submit"
          >
            {{
              subcategorySliderSelected
                ? 'Create Subcategory'
                : 'Create Category'
            }}
          </button>
        </fieldset>
      </form>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter, useRoute } from 'vue-router';
import { ref, reactive, watch, onBeforeUnmount, onMounted } from 'vue';
import Toggle from '../components/Toggle.vue';
import MainHeader from '../components/View-Header.vue';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import IconSearch from '../components/Icon-Selector.vue';
import ColorSelection from '../components/Color-Selection.vue';
import Rule from '../components/Categorization-Rule.vue';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: {
    MainHeader,
    IconSearch,
    ColorSelection,
    Rule,
    Toggle,
    SkeletonLoader,
  },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const route = useRoute();

    const searchTerm = ref('');
    const searchResults = reactive({});
    let showAutoCategorization = ref(false);
    const selectedRule = ref(null);
    const ruleItems = ref([]);
    let subcategorySliderSelected = ref(
      store.addSubcategoryToEditCategory ? true : false
    );
    const selectedParentCategory = ref('');
    const categoryName = ref('');
    let selectedCategoryIcon = '';
    let selectedCategoryIconColor = '';
    let incomeCategory = ref(false);
    let loading = ref(true);
    let loadingText = ref('Loading...');
    const preselectedParentCategory = ref();
    const defaultBudget = ref('');

    const setMode = (isSubcategory) => {
      subcategorySliderSelected.value = isSubcategory;
    };

    function searchIcons() {
      if (searchTerm.value.length < 3) return;
      const results = findIconDefinition({
        prefix: '',
        iconName: searchTerm.value,
      });
      searchResults.value = results;
    }

    const createNewRule = () => {
      const newRuleId = ruleItems.value.length + 1;
      ruleItems.value.push({ id: newRuleId, rule: {} });
    };

    const updateRulesList = (index, rule) => {
      ruleItems.value[index].rule = rule;
    };

    const deleteFromRulesList = (index) => {
      ruleItems.value.splice(index, 1);
    };

    const updateSelectedParentCategory = (selectedOption) => {
      selectedParentCategory.value = selectedOption;
    };

    const updateSelectedIcon = (selectedIcon) => {
      selectedCategoryIcon = selectedIcon;
    };

    const updateSelectedIconColor = (selectedColor) => {
      selectedCategoryIconColor = selectedColor;
    };

    const toggleIncome = (event) => {
      incomeCategory.value = event;
    };

    const createCategory = async () => {
      loadingText.value = 'Creating Category...';
      loading.value = true;
      try {
        let rules;
        if (subcategorySliderSelected.value === true) {
          if (
            categoryName.value !== null &&
            categoryName.value.length > 0 &&
            selectedParentCategory.value !== null
          ) {
            let newSubCategory = {
              CategoryID: selectedParentCategory.value.categoryID,
              Name: categoryName.value,
              Icon: selectedCategoryIcon,
              IconColor: selectedCategoryIconColor,
              DefaultBudget: parseFloat(defaultBudget.value) || 0,
            };
            await store
              .saveSubcategory(newSubCategory)
              .then(async (response) => {
                // loading.value = false;
                if (ruleItems.value.length > 0) {
                  rules = {
                    CategoryID: response.data.categoryID,
                    SubcategoryID: response.data.subcategoryID,
                    CategoryRules: ruleItems.value.map((ruleItem) => ({
                      Type: ruleItem.rule.type,
                      Comparison: ruleItem.rule.comparison,
                      ComparisonValue: ruleItem.rule.comparisonValue,
                    })),
                  };
                  await store.saveCategoryRules(rules);
                }

                await store.getAllCategories();
                router.push({ name: 'Categories' });
              });
            // await store.saveSubcategory(newSubCategory).then((response) => {
            //   loading.value = false;
            //   if (ruleItems.value.length > 0) {
            //     rules = {
            //       CategoryID: response.data.categoryID,
            //       SubcategoryID: response.data.subcategoryID,
            //       CategoryRules: ruleItems.value.map((ruleItem) => ({
            //         Type: ruleItem.rule.type,
            //         Comparison: ruleItem.rule.comparison,
            //         ComparisonValue: ruleItem.rule.comparisonValue,
            //       })),
            //     };
            //     store.saveCategoryRules(rules).then(() => {
            //       router.push({ name: 'Categories' });
            //     });
            //   } else {
            //     router.push({ name: 'Categories' });
            //   }
            // });
          } else {
            alert('Please enter a category name and select a parent category');
          }
        } else {
          if (categoryName.value !== null && categoryName.value.length > 0) {
            const categoryDetails = {
              Name: categoryName.value,
              Icon: selectedCategoryIcon,
              IconColor: selectedCategoryIconColor,
              Subcategories: [],
              Income: incomeCategory.value,
              DefaultBudget: parseFloat(defaultBudget.value) || 0,
            };
            await store.saveCategory(categoryDetails).then(async (response) => {
              // loading.value = false;
              if (ruleItems.value.length > 0) {
                rules = {
                  CategoryID: response.data.categoryID,
                  SubcategoryID: null,
                  CategoryRules: ruleItems.value.map((ruleItem) => ({
                    Type: ruleItem.rule.type,
                    Comparison: ruleItem.rule.comparison,
                    ComparisonValue: ruleItem.rule.comparisonValue,
                  })),
                };
                await store.saveCategoryRules(rules);
              }

              await store.getAllCategories();
              router.push({ name: 'Categories' });
            });
            // await store.saveCategory(categoryDetails).then((response) => {
            //   loading.value = false;
            //   if (ruleItems.value.length > 0) {
            //     rules = {
            //       CategoryID: response.data.categoryID,
            //       SubcategoryID: null,
            //       CategoryRules: ruleItems.value.map((ruleItem) => ({
            //         Type: ruleItem.rule.type,
            //         Comparison: ruleItem.rule.comparison,
            //         ComparisonValue: ruleItem.rule.comparisonValue,
            //       })),
            //     };
            //     store.saveCategoryRules(rules).then(() => {
            //       router.push({ name: 'Categories' });
            //     });
            //   } else {
            //     router.push({ name: 'Categories' });
            //   }
            // });
          } else {
            alert('Please enter a category name');
          }
        }
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    watch(ruleItems, () => {});

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      loading.value = false;

      if (route.query.id) {
        preselectedParentCategory.value = route.query.id;
        const foundCategory = store.categories.find(
          (x) => x.categoryID === Number(preselectedParentCategory.value)
        );
        if (foundCategory) {
          updateSelectedParentCategory(foundCategory);
        } else {
          console.warn(
            'No category found with ID:',
            preselectedParentCategory.value
          );
        }
      }
    });

    onBeforeUnmount(() => {
      store.updateAddSubcategoryToEditCategory(false);
    });

    return {
      store,
      router,
      searchIcons,
      searchTerm,
      searchResults,
      showAutoCategorization,
      selectedRule,
      createNewRule,
      ruleItems,
      updateRulesList,
      deleteFromRulesList,
      subcategorySliderSelected,
      setMode,
      selectedParentCategory,
      createCategory,
      categoryName,
      updateSelectedIcon,
      selectedCategoryIcon,
      updateSelectedIconColor,
      selectedCategoryIconColor,
      addSubcategory: () => {
        subcategorySliderSelected.value = true;
      },
      toggleIncome,
      loading,
      loadingText,
      defaultBudget,
      incomeCategory,
    };
  },
};
</script>

<style scoped>
.ac-view {
  max-width: 480px;
  margin: 0 auto;
}

.et-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 1.5rem;
  background: #f0f0f4;
  border-radius: var(--radius-pill);
  padding: 4px;
}
.dark-theme .et-tabs {
  background: #1a2535;
}

.et-tab {
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.6rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: #888;
  transition: all 0.2s ease;
}
.et-tab.active {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  box-shadow: 0 4px 12px rgba(127, 119, 221, 0.3);
}

.ac-header {
  text-align: center;
  padding: 0.5rem 0 1.5rem;
}
.ac-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.3rem;
  letter-spacing: -0.02em;
}
.dark-theme .ac-title {
  color: #5dcaa5;
}
.ac-sub {
  font-size: 0.82rem;
  color: #888;
  margin: 0;
}

.expandable-section {
  border: 1.5px solid #e7e7e7;
  border-radius: 12px;
  overflow: hidden;
}
.dark-theme .expandable-section {
  border-color: #2a3a4e;
}

.expandable-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background var(--transition-fast);
}
.expandable-header:hover {
  background: #fafafa;
}
.dark-theme .expandable-header:hover {
  background: #1a2535;
}

.expand-caret {
  color: #888;
  transition: transform 0.25s ease;
}
.expand-caret.expanded {
  transform: rotate(180deg);
  color: var(--color-accent-purple);
}

.expandable-body {
  border-top: 1.5px solid #f0f0f0;
  padding: 0.75rem 1rem;
  background: #fafafa;
}
.dark-theme .expandable-body {
  border-top-color: #2a3a4e;
  background: #1a2535;
}

.sub-slide-enter-from,
.sub-slide-leave-to {
  transform: translateY(-4px);
}
.sub-slide-enter-to,
.sub-slide-leave-from {
  opacity: 1;
  max-height: 600px;
  transform: translateY(0);
}
.ud-hint {
  display: block;
  font-size: 0.7rem;
  color: #888;
  margin-top: 4px;
  line-height: 1.4;
}
.dark-theme .ud-hint {
  color: #aaa;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <div>
      <MainHeader :title="'Add Category'">
        <template #left>
          <font-awesome-icon
            @click="
              store.addSubcategoryToEditCategory
                ? router.push({ name: 'EditCategory' })
                : router.push({ name: 'Categories' })
            "
            :icon="['fas', 'arrow-left-long']"
            class="pointer"
          />
        </template>
      </MainHeader>
    </div>
    <div class="view">
      <div>
        <Slider
          :options="sliderOptions"
          @sliderSelection="sliderOptionUpdated($event)"
          :activeOption="activeSliderOption"
          :hideOption="store.addSubcategoryToEditCategory ? true : false"
        ></Slider>
      </div>
      <div class="add-category-form">
        <div class="text-center pt-4 pb-4">
          <font-awesome-icon
            v-if="!subcategorySliderSelected"
            :icon="['fas', 'folder-plus']"
            class="view-icon add-cat-icon"
          />
          <font-awesome-icon
            v-else
            :icon="['fas', 'folder-tree']"
            class="view-icon add-cat-icon"
          />
        </div>
        <form @submit.prevent="createCategory">
          <fieldset>
            <div class="form-group">
              <div class="mt-1" v-if="subcategorySliderSelected">
                <label for="parentcategory" class="small-grey-text"
                  >Parent Category *</label
                >
                <div id="parentcategory">
                  <select
                    v-model="selectedParentCategory"
                    class="form-select grey-form-select"
                    required
                  >
                    <option
                      v-for="parentCategory in store.categories"
                      :key="parentCategory"
                      :value="parentCategory"
                      :selected="parentCategory"
                    >
                      {{ parentCategory.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="mt-1">
                <label for="categoryname" class="small-grey-text"
                  >Category Name *</label
                >
                <input
                  id="categoryname"
                  type="text"
                  class="input"
                  v-model="categoryName"
                  required
                />
              </div>
              <div class="mt-1">
                <label class="small-grey-text">Category Icon</label>
                <IconSearch
                  button-text="Choose Icon"
                  styleClass="form-dropdown"
                  @optionSelected="updateSelectedIcon($event)"
                />
              </div>
              <div class="mt-1">
                <label for="categorycolor" class="small-grey-text"
                  >Category Color</label
                >
                <ColorSelection
                  id="categorycolor"
                  button-text="Choose Color"
                  styleClass="form-dropdown"
                  @optionSelected="updateSelectedIconColor($event)"
                ></ColorSelection>
              </div>
              <div class="mt-1">
                <label class="small-grey-text">Auto Categorization</label>
                <div class="subcategorization-card">
                  <div
                    @click="showAutoCategorization = !showAutoCategorization"
                    class="w-100 flex align-center justify-between dropdown-padding"
                  >
                    <div>Apply Rules</div>
                    <font-awesome-icon
                      :icon="['fas', 'caret-down']"
                      class="arrow"
                      :class="{ active: showAutoCategorization }"
                    />
                  </div>
                  <div
                    v-if="showAutoCategorization"
                    class="subcategory-list-wrapper"
                  >
                    <button
                      aria-label="Add Rule"
                      role="button"
                      class="grey-button w-100"
                      type="button"
                      @click="createNewRule"
                      data-pt-name="AutoCategorizationRuleCreated"
                    >
                      Add Rule
                      <font-awesome-icon :icon="['fas', 'circle-plus']" />
                    </button>
                    <div v-for="(rule, index) in ruleItems" :key="rule">
                      <Rule
                        :currentRule="ruleItems[index].rule"
                        @addRule="createNewRule"
                        @updateRule="(rule) => updateRulesList(index, rule)"
                        @deleteRule="(rule) => deleteFromRulesList(index, rule)"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!subcategorySliderSelected" class="mt-1">
                <label for="isIncome" class="small-grey-text"
                  >Mark as Income</label
                >
                <Toggle id="isIncome" @toggleSelected="toggleIncome($event)" />
              </div>
              <div class="mt-1">
                <button
                  aria-label="Create Category"
                  role="button"
                  class="blue-button mt-4 w-100"
                  type="submit"
                >
                  Create Category
                </button>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter, useRoute } from 'vue-router';
import { ref, reactive, watch, onBeforeUnmount, onMounted } from 'vue';
import Toggle from '../components/Toggle.vue';

import MainHeader from '../components/View-Header.vue';
import Slider from '../components/Slider.vue';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import IconSearch from '../components/Icon-Selector.vue';
import ColorSelection from '../components/Color-Selection.vue';
import Rule from '../components/Categorization-Rule.vue';
import { Category_Types } from '../utils/constants.js';

export default {
  components: {
    MainHeader,
    Slider,
    IconSearch,
    ColorSelection,
    Rule,
    Toggle,
  },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const sliderOptions = [
      { label: 'Add Category', disabled: false },
      { label: 'Add Subcategory', disabled: true },
    ];
    const searchTerm = ref('');
    const searchResults = reactive({});
    let showAutoCategorization = ref(false);
    const selectedRule = ref(null);
    const ruleItems = ref([]);
    let subcategorySliderSelected = ref(false);
    const selectedParentCategory = ref('');
    const categoryName = ref('');
    let selectedCategoryIcon = '';
    let selectedCategoryIconColor = '';
    let showEditSubcategories = ref(false);
    let incomeCategory = ref(false);
    let loading = ref(true);
    let loadingText = ref('Loading...');
    const route = useRoute();
    const preselectedParentCategory = ref();

    if (store.addSubcategoryToEditCategory) {
      subcategorySliderSelected = ref(true);
    }
    let activeSliderOption = ref(
      subcategorySliderSelected.value === true ? 1 : 0
    );

    function searchIcons() {
      if (searchTerm.value.length < 3) {
        return;
      }
      const results = findIconDefinition({
        prefix: '',
        iconName: searchTerm.value,
      });
      searchResults.value = results;
    }

    const createNewRule = () => {
      const newRuleId = ruleItems.value.length + 1;
      ruleItems.value.push({ id: newRuleId, rule: {} });
    };

    const updateRulesList = (index, rule) => {
      ruleItems.value[index].rule = rule;
    };

    const deleteFromRulesList = (index) => {
      ruleItems.value.splice(index, 1);
    };

    const sliderOptionUpdated = (selection) => {
      if (selection === Category_Types.subCategory) {
        subcategorySliderSelected.value = true;
        activeSliderOption.value = 1;
      } else {
        subcategorySliderSelected.value = false;
        activeSliderOption.value = 0;
      }
    };

    const updateSelectedParentCategory = (selectedOption) => {
      selectedParentCategory.value = selectedOption;
    };

    const updateSelectedIcon = (selectedIcon) => {
      selectedCategoryIcon = selectedIcon;
    };

    const updateSelectedIconColor = (selectedColor) => {
      selectedCategoryIconColor = selectedColor;
    };

    const toggleIncome = (event) => {
      incomeCategory.value = event;
    };

    const addSubcategory = () => {
      store.updateAddSubcategoryToEditCategory(true);
      subcategorySliderSelected.value = true;
      activeSliderOption.value = 1;
    };

    const createCategory = async () => {
      loadingText.value = 'Creating Category...';
      loading.value = true;
      try {
        let categoryDetails;
        let rules;
        if (subcategorySliderSelected.value === true) {
          if (
            categoryName.value !== null &&
            categoryName.value.length > 0 &&
            selectedParentCategory.value !== null
          ) {
            let newSubCategory = {
              CategoryID: selectedParentCategory.value.categoryID,
              Name: categoryName.value,
              Icon: selectedCategoryIcon,
              IconColor: selectedCategoryIconColor,
            };

            await store.saveSubcategory(newSubCategory).then((response) => {
              loading.value = false;

              if (ruleItems.value.length > 0) {
                rules = {
                  CategoryID: response.data.categoryID,
                  SubcategoryID: response.data.subcategoryID,
                  CategoryRules: ruleItems.value.map((ruleItem) => ({
                    Type: ruleItem.rule.type,
                    Comparison: ruleItem.rule.comparison,
                    ComparisonValue: ruleItem.rule.comparisonValue,
                  })),
                };
                store.saveCategoryRules(rules).then(() => {
                  router.push({ name: 'Categories' });
                });
              } else {
                router.push({ name: 'Categories' });
              }
            });
          } else {
            alert('Please enter a category name and select a parent category');
          }
        } else {
          if (categoryName.value !== null && categoryName.value.length > 0) {
            categoryDetails = {
              Name: categoryName.value,
              Icon: selectedCategoryIcon,
              IconColor: selectedCategoryIconColor,
              Subcategories: [],
              Income: incomeCategory.value,
            };

            await store.saveCategory(categoryDetails).then((response) => {
              loading.value = false;
              if (ruleItems.value.length > 0) {
                rules = {
                  CategoryID: response.data.categoryID,
                  SubcategoryID: null,
                  CategoryRules: ruleItems.value.map((ruleItem) => ({
                    Type: ruleItem.rule.type,
                    Comparison: ruleItem.rule.comparison,
                    ComparisonValue: ruleItem.rule.comparisonValue,
                  })),
                };
                store.saveCategoryRules(rules).then(() => {
                  router.push({ name: 'Categories' });
                });
              } else {
                router.push({ name: 'Categories' });
              }
            });
          } else {
            alert('Please enter a category name');
          }
        }
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    watch(ruleItems, () => {});

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      loading.value = false;

      if (route.query.id) {
        preselectedParentCategory.value = route.query.id;
        const foundCategory = store.categories.find(
          (x) => x.categoryID === Number(preselectedParentCategory.value)
        );
        if (foundCategory) {
          updateSelectedParentCategory(foundCategory);
        } else {
          console.warn(
            'No category found with ID:',
            preselectedParentCategory.value
          );
        }
      }
    });

    onBeforeUnmount(() => {
      store.updateAddSubcategoryToEditCategory(false);
    });

    return {
      store,
      router,
      sliderOptions,
      searchIcons,
      searchTerm,
      searchResults,
      showAutoCategorization,
      selectedRule,
      createNewRule,
      ruleItems,
      updateRulesList,
      deleteFromRulesList,
      sliderOptionUpdated,
      subcategorySliderSelected,
      selectedParentCategory,
      createCategory,
      categoryName,
      updateSelectedIcon,
      selectedCategoryIcon,
      updateSelectedIconColor,
      selectedCategoryIconColor,
      showEditSubcategories,
      addSubcategory,
      activeSliderOption,
      toggleIncome,
      loading,
      loadingText,
    };
  },
};
</script>

<style scoped>
.subcategorization-card {
  border-radius: 0.5em;
  margin-bottom: 1em;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.light-theme .subcategorization-card {
  background: white;
  color: #004751;
  background: #f1f1f1;
}

.dark-theme .subcategorization-card {
  background: #171e29;
  border: 1px solid #d4d2d2;
  color: #d4d2d2;
}

.light-theme .subcategorization-card i {
  color: #004751;
}

.dark-theme .subcategorization-card i {
  color: #d4d2d2;
}

.subcategory-list-wrapper {
  width: 100%;
  padding: 10px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.1);
}

.light-theme .subcategory-list-wrapper {
  border: 1px solid #f1f1f1;
  background: white;
}

.dark-theme .subcategory-list-wrapper {
  background: transparent;
}

.dropdown-padding {
  padding: 10px;
}

.add-cat-icon {
  color: #e8f44a;
}

.arrow {
  margin-left: 0.5em;
  transition: transform 0.2s;
}

.arrow.active {
  transform: rotate(-180deg);
}
</style> -->
